import { useEffect, useState } from 'react';
import type { PedidoVista } from '@shared/types';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 8 — "No entregado": el pedido que se fue y volvió.
 *
 * Cancela el pedido y restituye el stock, así que pide **PIN del dueño** y un motivo escrito: es
 * comida que salió de la cocina y dinero que puede quedar por devolver (DT-072). No es una
 * incidencia de operación, es un evento que alguien tiene que firmar.
 *
 * ## Sprint 99 — Sale de `DespachoPage` para vivir donde se usa
 *
 * Vivía dentro de la pantalla de Despacho, que era la única que podía ofrecer esta acción. Con el
 * tablero de A domicilio como fuente única, la decisión se toma mirando la columna "En ruta" — que
 * es donde el operador ve el pedido cuando el repartidor le llama para decir que no había nadie.
 *
 * La regla no cambia: sigue siendo sólo para el dueño, con PIN y con motivo.
 */
export function NoEntregadoModal({
  pedido,
  onClose,
  onConfirmado,
}: {
  pedido: PedidoVista | null;
  onClose: () => void;
  onConfirmado: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [pin, setPin] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (pedido) {
      setMotivo('');
      setPin('');
      setError(null);
    }
  }, [pedido]);

  if (!pedido) return null;

  const enviar = async () => {
    if (motivo.trim().length < 5) {
      setError('El motivo debe tener al menos 5 caracteres.');
      return;
    }
    if (!/^\d{4}$/.test(pin)) {
      setError('PIN dueña: 4 dígitos.');
      return;
    }
    setEnviando(true);
    setError(null);
    const r = await window.api.pedidos.marcarNoEntregado({
      pedidoId: pedido.id,
      motivo: motivo.trim(),
      pinDueno: pin,
    });
    setEnviando(false);
    if (!r.ok) {
      setError(traducirErrorBackend(r.error));
      return;
    }
    onConfirmado();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="card w-full max-w-md">
        <h2 className="text-lg font-bold mb-1 text-rose-700">Marcar como NO entregado</h2>
        <p className="text-xs text-slate-500 mb-3">
          Pedido #{String(pedido.numeroPedido).padStart(5, '0')} — {pedido.clienteNombre}.
          Esta acción cancela el pedido y restituye stock. Requiere PIN dueña.
        </p>
        <label className="text-sm font-medium">Motivo</label>
        <textarea
          autoFocus
          className="input w-full mt-1 min-h-[80px]"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Ej: Cliente no contestó, dirección equivocada, no había nadie…"
        />
        <label className="text-sm font-medium mt-3 block">PIN dueña (4 dígitos)</label>
        <input
          type="password"
          inputMode="numeric"
          maxLength={4}
          className="input w-32 mt-1"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
        />
        {error && <div className="text-rose-700 text-sm mt-2">{error}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost" disabled={enviando}>
            Cancelar
          </button>
          <button
            onClick={enviar}
            className="btn-primary bg-rose-600 hover:bg-rose-700"
            disabled={enviando}
          >
            {enviando ? 'Procesando…' : 'Confirmar no entregado'}
          </button>
        </div>
      </div>
    </div>
  );
}
